import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface MobileQuickActionsProps {
    hasAISuggestions?: boolean;
}

export const MobileQuickActions: React.FC<MobileQuickActionsProps> = ({
    hasAISuggestions = false
}) => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    // Cierre automático al cambiar de ruta
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    // Escuchar evento personalizado del botón atrás de Android / Capacitor
    useEffect(() => {
        const handleCustomBack = (e: Event) => {
            if (isOpen) {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
            }
        };

        window.addEventListener('kl-back-button', handleCustomBack);
        return () => window.removeEventListener('kl-back-button', handleCustomBack);
    }, [isOpen]);

    const handleBugReport = (e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const origin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        setIsOpen(false);
        window.dispatchEvent(new CustomEvent('open-bug-report', { detail: { origin } }));
    };

    const handleAIAssistant = (e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const origin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        setIsOpen(false);
        window.dispatchEvent(new CustomEvent('open-ai-assistant', { detail: { origin } }));
    };

    // Altura del panel de acciones (padding 20+14 + botón 92 = 126px)
    const panelHeight = 126;

    return (
        <div
            className="kl-quick-actions"
            data-quick-actions-open={isOpen ? 'true' : 'false'}
            style={{ position: 'relative' }}
        >
            {/* Velo detrás del panel sobre el contenido (no sobre la barra inferior) */}
            {isOpen && (
                <div
                    onClick={() => setIsOpen(false)}
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 'calc(56px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))',
                        background: 'rgba(15, 23, 42, 0.38)',
                        zIndex: 42,
                        transition: 'opacity 220ms ease-out'
                    }}
                />
            )}

            {/* Pestaña / Tirador fijo que se desplaza junto al panel */}
            <button
                type="button"
                data-quick-actions-close="true"
                onClick={() => setIsOpen(prev => !prev)}
                aria-label={isOpen ? 'Cerrar acciones rápidas' : 'Abrir acciones rápidas'}
                style={{
                    position: 'fixed',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: isOpen
                        ? `calc(56px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)) + ${panelHeight}px)`
                        : 'calc(56px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))',
                    width: '60px',
                    height: '22px',
                    background: '#ffffff',
                    border: '1px solid #E2E8F0',
                    borderBottom: 'none',
                    borderRadius: '14px 14px 0 0',
                    boxShadow: '0 -2px 6px rgba(0,0,0,.07)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 47,
                    padding: 0,
                    transition: 'bottom 220ms ease-out',
                    WebkitTapHighlightColor: 'transparent'
                }}
            >
                {/* Zona táctil ampliada a 44px de alto */}
                <span
                    style={{
                        position: 'absolute',
                        top: '-11px',
                        bottom: '-11px',
                        left: 0,
                        right: 0,
                        pointerEvents: 'auto'
                    }}
                />

                {/* Flecha Chevron (Arriba si cerrado, Abajo si abierto) */}
                <svg
                    viewBox="0 0 24 24"
                    style={{
                        width: '20px',
                        height: '20px',
                        fill: 'none',
                        stroke: '#475569',
                        strokeWidth: 2.4,
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                        pointerEvents: 'none',
                        transition: 'transform 220ms ease-out'
                    }}
                >
                    {isOpen ? (
                        <path d="M6 9l6 6 6-6" />
                    ) : (
                        <path d="M18 15l-6-6-6 6" />
                    )}
                </svg>

                {/* Punto verde solo si existe señal real de sugerencias */}
                {hasAISuggestions && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '3px',
                            right: '6px',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#16A34A'
                        }}
                    />
                )}
            </button>

            {/* Panel emergente anclado encima de la barra inferior */}
            <div
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 'calc(56px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))',
                    background: '#ffffff',
                    borderRadius: '18px 18px 0 0',
                    padding: '20px 12px 14px',
                    boxShadow: '0 -6px 18px rgba(0,0,0,.14)',
                    zIndex: 45,
                    transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
                    opacity: isOpen ? 1 : 0,
                    visibility: isOpen ? 'visible' : 'hidden',
                    transition: 'transform 220ms ease-out, opacity 220ms ease-out, visibility 220ms',
                    boxSizing: 'border-box'
                }}
            >
                {/* Grilla de 2 columnas con 10px de separación */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '10px'
                    }}
                >
                    {/* Botón 1: Reportar falla técnica */}
                    <button
                        type="button"
                        onClick={handleBugReport}
                        style={{
                            height: '92px',
                            background: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '8px 4px',
                            cursor: 'pointer',
                            WebkitTapHighlightColor: 'transparent'
                        }}
                    >
                        {/* Ícono sólido 44x44 */}
                        <div
                            style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '14px',
                                background: '#DC2626',
                                color: '#FFFFFF',
                                boxShadow: '0 2px 8px rgba(0,0,0,.18)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                ['--kl-fab-bg' as any]: '#DC2626'
                            }}
                        >
                            <svg
                                width="29"
                                height="29"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ width: '29px', height: '29px', flexShrink: 0 }}
                            >
                                <rect x="2.5" y="3.5" width="15" height="12" rx="2" />
                                <path d="M2.5 7.5h15" />
                                <path d="m8 9.6 4 4" />
                                <path d="m12 9.6-4 4" />
                                <path d="M18 12.5 22.5 20.5H13.5z" fill="var(--kl-fab-bg, #DC2626)" />
                                <path d="M18 15.8v2" />
                                <path d="M18 19.5h.01" />
                            </svg>
                        </div>
                        {/* Etiqueta 13px / 700 */}
                        <span
                            style={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: '#0F172A',
                                textAlign: 'center',
                                lineHeight: 1.15
                            }}
                        >
                            Reportar falla técnica
                        </span>
                    </button>

                    {/* Botón 2: Asistente IA */}
                    <button
                        type="button"
                        onClick={handleAIAssistant}
                        style={{
                            height: '92px',
                            background: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '8px 4px',
                            cursor: 'pointer',
                            position: 'relative',
                            WebkitTapHighlightColor: 'transparent'
                        }}
                    >
                        {/* Ícono degradado azul->violeta 44x44 */}
                        <div
                            style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                                color: '#FFFFFF',
                                boxShadow: '0 2px 8px rgba(0,0,0,.18)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                position: 'relative'
                            }}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                style={{
                                    width: '22px',
                                    height: '22px',
                                    fill: 'none',
                                    stroke: 'currentColor',
                                    strokeWidth: 2,
                                    strokeLinecap: 'round',
                                    strokeLinejoin: 'round'
                                }}
                            >
                                <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" />
                            </svg>

                            {/* Punto verde en el botón del asistente si existe señal real */}
                            {hasAISuggestions && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-2px',
                                        right: '-2px',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        background: '#16A34A',
                                        border: '2px solid #ffffff'
                                    }}
                                />
                            )}
                        </div>

                        {/* Etiqueta 13px / 700 */}
                        <span
                            style={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: '#0F172A',
                                textAlign: 'center',
                                lineHeight: 1.15
                            }}
                        >
                            Asistente IA
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};
